import type { DtoApplication } from "@porchpass/api-client";
import type { User } from "firebase/auth";
import { File02Icon, FormTableSummaryCard } from "@porchpass/ui";

export default ({
  application,
  authUser,
  selectApplication,
  index = 0,
}: {
  application: DtoApplication;
  authUser: User | null;
  selectApplication: (application: DtoApplication) => void;
  index?: number;
}) => {
  const currentBorrower = application.borrowers?.find(
    (borrower) => borrower.user_id === authUser?.uid,
  );

  const getTitleDisplay = () => {
    if (currentBorrower?.status === "INVITED") {
      return "Pending Invite";
    }

    const mainBorrower = application.borrowers?.find(
      (borrower) => borrower.is_primary,
    );
    return `${mainBorrower?.first_name}'s Application`;
  };

  const areAllBorrowersSigned = () =>
    application.borrowers?.every(
      (borrower) =>
        borrower.status === "SIGNED" ||
        borrower.status === "WAITING_ON_ESIGNATURE_SERVICE",
    );

  const getStatus = () => {
    if (currentBorrower?.status === "INVITED") {
      return "";
    }

    switch (application.status) {
      case "SUBMITTED":
      case "REVIEWED":
        return "Submitted";
      case "WAITING_ON_ESIGNATURE_SERVICE":
      case "SENT_FOR_SIGNATURE":
        return areAllBorrowersSigned() ? "Signature Sent" : "In Progress";
      case "STARTED":
      default:
        return "In Progress";
    }
  };

  const borrowerDisplay = () => {
    if (currentBorrower?.is_primary) {
      return "Main Borrower";
    }
    return "Co-Borrower";
  };

  const getFormattedDate = () => {
    const formattedDate = new Date(application.created_at ?? "").toLocaleString(
      "en-US",
      {
        hour: "numeric",
        minute: "numeric",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour12: true,
      },
    );
    return formattedDate.replace("at ", "");
  };

  const status = getStatus();
  const isSubmitted =
    application.status === "SUBMITTED" || application.status === "REVIEWED";
  const isInvited = currentBorrower?.status === "INVITED";

  return (
    <FormTableSummaryCard
      index={index}
      title={getTitleDisplay()}
      icon={
        <File02Icon
          variant="bulk-rounded"
          size={16}
          className={isSubmitted ? "text-[#4a5466]" : "text-primary"}
        />
      }
      iconTone={isSubmitted ? "default" : "primary"}
      highlight={isSubmitted || isInvited ? "none" : "primary"}
      badges={
        status
          ? [
              {
                label: status,
                variant: isSubmitted ? "default" : "primary",
              },
            ]
          : undefined
      }
      subtitles={[
        borrowerDisplay(),
        `ID: ${application.id}`,
        getFormattedDate(),
      ]}
      onClick={() => selectApplication(application)}
    />
  );
};
